import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import { ROUTES } from "../../routes";
import { RootState } from "../../app/store";
import DayTimeSlots from "./DayTimeSlots";
import {
  groupTimeSlotsByDate,
  sortTimeSlotsByTime,
  filterUpcomingTimeSlots,
} from "../../features/utils/timeSlotUtils";
import { fetchAvailableTimeSlots } from "../../features/sessions/slice-extensions/sessions-slice-extension";
import { useEffect } from "react";
import { Clock } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import { TableRowsSkeleton } from "../skeleton/Skeleton";

const AvailableTimeSlotsCard = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { availableTimeSlots, initializationStatus } =
    useSelector((state: RootState) => state.sessions.timeSlots);

  const timeSlots = availableTimeSlots;
  const isInitializing = initializationStatus === "initializing";
  const isFailed = initializationStatus === "failed";

  useEffect(() => {
    if (initializationStatus === "not-initialized") {
      dispatch(fetchAvailableTimeSlots());
    }
  }, [dispatch, initializationStatus]);

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <svg
            className="lg:size-16"
            fill="none"
            height="48"
            viewBox="0 0 48 48"
            width="48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.75 10.5C13.1642 10.5 13.5 10.8358 13.5 11.25C13.5 11.6642 13.1642 12 12.75 12H12C11.1716 12 10.5 12.6716 10.5 13.5V34.5C10.5 35.3284 11.1716 36 12 36H19.9082C20.1909 36 20.4442 36.174 20.5605 36.4316C20.7804 36.9187 20.4406 37.4998 19.9062 37.5H12C10.3431 37.5 9 36.1569 9 34.5V13.5C9 11.8431 10.3431 10.5 12 10.5H12.75ZM16.5 28.5C17.3284 28.5 18 29.1716 18 30C18 30.8284 17.3284 31.5 16.5 31.5C15.6716 31.5 15 30.8284 15 30C15 29.1716 15.6716 28.5 16.5 28.5ZM16.5 21.75C17.3284 21.75 18 22.4216 18 23.25C18 24.0784 17.3284 24.75 16.5 24.75C15.6716 24.75 15 24.0784 15 23.25C15 22.4216 15.6716 21.75 16.5 21.75ZM24 21.75C24.1749 21.75 24.2184 21.9611 24.0811 22.0693C23.5957 22.4518 23.1405 22.8707 22.7197 23.3223C22.6401 23.4078 22.5 23.3669 22.5 23.25C22.5 22.4216 23.1716 21.75 24 21.75ZM36 10.5C37.6569 10.5 39 11.8431 39 13.5V20.71C39 21.2838 38.3258 21.6161 37.8389 21.3125C37.6317 21.1833 37.5 20.958 37.5 20.7139V13.5C37.5 12.6716 36.8284 12 36 12H35.25C34.8358 12 34.5 11.6642 34.5 11.25C34.5 10.8358 34.8358 10.5 35.25 10.5H36ZM35.25 16.5C35.6642 16.5 36 16.8358 36 17.25C36 17.6642 35.6642 18 35.25 18H12.75C12.3358 18 12 17.6642 12 17.25C12 16.8358 12.3358 16.5 12.75 16.5H35.25ZM16.502 7.5C17.2466 7.5 17.9971 7.875 17.9971 9C17.9971 10.125 18.002 11.5252 18.002 12C17.9969 13.125 17.257 13.5 16.502 13.5C15.747 13.4999 14.9969 13.1249 15.002 12C15.002 11.5252 15.0029 10.125 15 9C14.9971 7.87508 15.7466 7.50005 16.502 7.5ZM31.502 7.5C32.2466 7.5 32.9971 7.875 32.9971 9C32.9971 10.125 33.002 11.5252 33.002 12C32.9969 13.125 32.257 13.5 31.502 13.5C30.747 13.4999 29.9969 13.1249 30.002 12C30.002 11.5252 30.0029 10.125 30 9C29.9971 7.87508 30.7466 7.50005 31.502 7.5ZM27.75 10.5C28.1642 10.5 28.5 10.8358 28.5 11.25C28.5 11.6642 28.1642 12 27.75 12H20.25C19.8358 12 19.5 11.6642 19.5 11.25C19.5 10.8358 19.8358 10.5 20.25 10.5H27.75Z"
              fill="#808080"
            />
            <path
              d="M31.5 21C37.299 21 42 25.701 42 31.5C42 37.299 37.299 42 31.5 42C25.701 42 21 37.299 21 31.5C21 25.701 25.701 21 31.5 21ZM31.5 24C30.6716 24 30 24.6716 30 25.5V31.5C30 31.8978 30.1581 32.2792 30.4395 32.5605L33.4395 35.5605L33.5537 35.6641C34.1429 36.1442 35.0115 36.1096 35.5605 35.5605C36.1096 35.0115 36.1442 34.1429 35.6641 33.5537L35.5605 33.4395L33 30.8789V25.5C33 24.6716 32.3284 24 31.5 24Z"
              fill="#FF7733"
            />
          </svg>
          <h2>{t("sessions.availableTimeSlots")}</h2>
        </div>
      </div>
      <p className="mt-2 lg:text-xl">
        {t("sessions.browseTimeSlotsDescription")}
      </p>

      {isInitializing && (
        <div className="mt-4 lg:mt-6">
          <TableRowsSkeleton rows={4} />
        </div>
      )}

      {!isInitializing && (
        <div className="mt-4 space-y-4 lg:mt-6 lg:space-y-6">
          {(() => {
            const upcomingTimeSlots = filterUpcomingTimeSlots(timeSlots);
            const groupedTimeSlots = groupTimeSlotsByDate(upcomingTimeSlots);
            const sortedDates = Object.keys(groupedTimeSlots).sort();

            // Show only first 2 days for the card preview
            const previewDates = sortedDates.slice(0, 2);

            if (isFailed || previewDates.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-8 gap-4">
                  <img
                    src="/assets/images/icons/available-slots.svg"
                    alt={t("sessions.noAvailableTimeSlots")}
                    className="w-32 h-32 lg:w-40 lg:h-40"
                  />
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-grey-20 dark:text-grey-90 mb-2">
                      {t("sessions.noAvailableTimeSlots")}
                    </h3>
                    <p className="text-grey-40 dark:text-grey-80">
                      <Trans
                        i18nKey="sessions.visitYourCalendar"
                        components={{
                          calendarLink: (
                            <button
                              className="hover:text-primary-40 dark:hover:text-primary-60 font-bold underline underline-offset-2 ml-1"
                              onClick={() => navigate(ROUTES.SESSIONS)}
                            />
                          ),
                        }}
                      />
                    </p>
                  </div>
                </div>
              );
            }

            return (
              <>
                {previewDates.map((dateKey) => {
                  const date = new Date(dateKey);
                  const daySlots = sortTimeSlotsByTime(groupedTimeSlots[dateKey]);
                  return (
                    <DayTimeSlots key={dateKey} date={date} timeSlots={daySlots} />
                  );
                })}
                {/* Footnote only makes sense when slots are shown — the empty
                    state above already links to the calendar. */}
                <p>
                  <Trans
                    i18nKey="sessions.notAllSessionsShown"
                    components={{
                      calendarLink: (
                        <button
                          className="text-primary-40 dark:text-primary-90 font-bold underline underline-offset-2 hover:no-underline"
                          onClick={() => navigate(ROUTES.CALENDAR)}
                        />
                      ),
                    }}
                  />
                </p>
              </>
            );
          })()}
        </div>
      )}

      <div className="mt-4 lg:mt-6 flex justify-center">
        <button
          className="btn btn-secondary"
          onClick={() => navigate(ROUTES.TIME_SLOTS)}
        >
          <Clock size={20} strokeWidth={2.5} />
          <span>{t("sessions.viewAllTimeSlots")}</span>
        </button>
      </div>
    </div>
  );
};
export default AvailableTimeSlotsCard;
